/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import Card from '@/components/card';
import BreadcrumbComponent from '@/components/breadcrumb';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  notification,
  Row,
  Select,
  Table,
} from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { IOrderItem } from '@/interfaces/order';
import locale from 'antd/es/date-picker/locale/th_TH';
import { formatNumber } from '@/utils/dataUtils';
import { round } from 'lodash';
import Title from '@/components/title';
import { ICndnItem } from '@/interfaces/cndn';
import { useMutation } from '@tanstack/react-query';
import {
  getCndnTypeName,
  getCndnTypeOptionsWithCreditReturn,
} from '@/helpers/cndnHelper';
import { ECndnReason, ECndnType } from '@/enums/ECndn';
import CustomerSelector from '@/components/selector/customer-selector';
import { ICustomer } from '@/interfaces/customer';
import ShippingAddressSelector from '@/components/selector/shipping-address-selector';
import { getShippingFullAddress } from '@/utils/shippingAddressUtils';
import { ICndnFreeForm } from '@/interfaces/cndnFreeForm';
import { DeleteFilled, EditFilled } from '@ant-design/icons';
import OrderCndnFreeFomTable from './components/order-table';
import UserSelector from '@/components/selector/user-selector';
import dayjs from 'dayjs';
import { useAuth } from '@/contexts/AuthContext';
import { EUserRole } from '@/enums/EUserRole';
import { addCndnManual } from '@/adapters/cndn-manual';
// import { getQueryParam } from '@/utils/pathUtils';

const CreateCndnFreeForm = () => {
  const { userInfo } = useAuth();
  // const _orderId = getQueryParam('orderId');
  const navigate = useNavigate();
  const [mainForm] = Form.useForm<Partial<ICndnFreeForm>>();
  const [form] = Form.useForm();
  const [discount, setDiscount] = useState(0);
  const [vatRate, setVatRate] = useState(7);
  const [editingKey, setEditingKey] = useState<string>('');
  const [editingIndex, setEditingIndex] = useState<string>('');
  const [dataItem, setDataItem] = useState<Partial<ICndnItem>[]>([]);
  const [orderData, setOrderData] = useState<Partial<IOrderItem>[]>([]);
  const [orderSummary, setOrderSummary] = useState(0);

  const isEditing = (record: Partial<IOrderItem>) => record.key === editingKey;

  const _type = Form.useWatch('type', mainForm);

  // const { data: order } = useQuery<IOrder, Error>({
  //   queryKey: ['get-order', orderId],
  //   queryFn: async () => await getOrder(Number(orderId)),
  //   enabled: !!orderId,
  // });

  const edit = (record: Partial<ICndnItem>) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.key || '');
  };

  const save = async (key: string) => {
    try {
      const row = (await form.validateFields()) as Partial<ICndnItem>;
      const newData = [...dataItem];
      const index = newData.findIndex(item => key === item.key || '');
      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, {
          ...item,
          unitPrice: round(Number(item.unitPrice || 0), 2),
          quantity: round(Number(item.quantity || 0), 2),
          ...row,
        });
        setDataItem(newData);
        setEditingKey('');
        setEditingIndex('');
      } else {
        newData.push(row);
        setDataItem(newData);
        setEditingIndex('');
      }
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };

  interface EditableCellProps extends React.HTMLAttributes<any> {
    editing: boolean;
    dataIndex: string;
    title: string;
    inputType: 'number' | 'text';
    record: Partial<ICndnItem>;
    index: number;
  }

  const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
    record,
    editing,
    dataIndex,
    title,
    inputType,
    children,
    ...restProps
  }) => {
    const inputNode =
      inputType === 'number' ? (
        <InputNumber
          className="w-full"
          type="number"
          onBlur={() => save(record.key || '')}
          onPressEnter={() => save(record.key || '')}
          autoFocus
          controls={false}
          decimalSeparator={'.'}
          precision={2}
          style={{ textAlignLast: 'right' }}
        />
      ) : (
        <Input onBlur={() => save(record.key || '')} />
      );

    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[
              {
                required: true,
                message: `Please Input ${title}!`,
              },
            ]}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  const columns = [
    {
      title: 'รหัสสินค้า',
      dataIndex: 'productCode',
      width: 120,
      className: 'font-bold',
      align: 'center',
    },
    {
      title: 'สินค้า',
      dataIndex: 'productName',
      className: 'font-bold',
    },
    {
      title: 'Lot Number',
      dataIndex: 'lotNumber',
      width: 120,
      className: 'font-bold',
      align: 'center',
    },
    {
      title: 'จำนวน Package',
      width: 120,
      dataIndex: 'package',
      align: 'right',
      render: (_: number, rec: ICndnItem) =>
        formatNumber(round(rec.orderQuantity / rec.conversion, 2), 2),
    },
    {
      title: 'หน่วย Package',
      width: 120,
      dataIndex: 'packageUnitName',
    },
    {
      title: 'จำนวน',
      width: 120,
      dataIndex: 'quantity',
      align: 'right',
      render: (_: number) => formatNumber(_, 2),
    },
    {
      title: 'จำนวนที่คืน',
      width: 120,
      editable: editingIndex === 'orderQuantity',
      dataIndex: 'orderQuantity',
      className: 'font-bold text-blue-700 bg-red-200',
      align: 'right',
      hidden: _type !== ECndnType.CREDIT_RETURN,
      onHeaderCell: () => ({
        className: '!bg-red-700 !text-white', // Use the class here
      }),
      render: (_: number, rec: IOrderItem) => (
        <div
          className="font-bold w-full cursor-pointer flex justify-end gap-2"
          onClick={() => onRowClick(rec, 'orderQuantity')}
        >
          {formatNumber(_, 2)}
          <EditFilled className="text-gray-300" />
        </div>
      ),
    },
    {
      title: 'หน่วยขาย',
      width: 100,
      dataIndex: 'baseUnitName',
    },
    {
      title: 'ราคา',
      width: 120,
      dataIndex: 'orderUnitPrice',
      align: 'right',
      render: (_: number) => formatNumber(_, 2),
    },
    {
      title: `ราคาที่${getCndnTypeName(Number(_type))}`,
      editable: editingIndex === 'unitPrice',
      width: 120,
      dataIndex: 'unitPrice',
      align: 'right',
      hidden: _type == ECndnType.CREDIT_RETURN,
      onHeaderCell: () => ({
        className: '!bg-red-700 !text-white', // Use the class here
      }),
      className: 'font-bold text-blue-700 bg-red-200',
      render: (_: number, rec: ICndnItem) => (
        <div
          className="font-bold w-full cursor-pointer"
          onClick={() => onRowClick(rec, 'unitPrice')}
        >
          {formatNumber(_, 2)}
        </div>
      ),
    },

    {
      title: 'รวมเงิน',
      width: 150,
      dataIndex: 'totalPrice',
      align: 'right',
      className: 'font-bold',
      render: (_: number, rec: ICndnItem) => {
        console.log(rec);
        return formatNumber(
          round(rec.orderQuantity * (rec.unitPrice || 0), 2),
          2,
        );
      },
    },
    {
      title: '',
      width: 40,
      dataIndex: 'action',
      align: 'center',
      render: (_: string, rec: IOrderItem) => (
        <DeleteFilled
          className="text-red-700"
          onClick={() => onDeleteCndnItem(rec)}
        />
      ),
    },
  ];
  const mergedColumns: any = columns.map(col => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: IOrderItem) => ({
        record,
        inputType:
          typeof record[col.dataIndex as keyof IOrderItem] === 'number' &&
          col.dataIndex === editingIndex
            ? 'number'
            : 'text',
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });
  const onDeleteCndnItem = (item: IOrderItem) => {
    setDataItem(prev => prev.filter(p => p.key !== item.key));
  };

  const onRowClick = (record: Partial<IOrderItem>, index: string) => {
    edit(record);
    setEditingIndex(index);
  };

  const mutation = useMutation({
    mutationFn: async (data: Partial<ICndnFreeForm>) =>
      await addCndnManual(data),
    mutationKey: ['create-cndn-manual'],
    onSuccess: data => {
      if (data?.status != 200) {
        throw new Error();
      } else {
        notification.success({
          message: 'บันทึกใบลดหนี้/เพิ่มหนี้สำเร็จ',
        });
        navigate(`/cndn-free-form/${data?.data?.id}`);
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const handleUpdate = async () => {
    mainForm
      .validateFields()
      .then(async (values: Partial<ICndnFreeForm>) => {
        const allUnitPricesZero = dataItem.every(item => item.unitPrice === 0);
        if (allUnitPricesZero) {
          message.warning('ต้องเพิ่มหนี้/ลดหนี้ อย่างน้อย 1 รายการ');
          return;
        }
        values.orderItems = orderData.map(d => {
          const orderItem: Partial<IOrderItem> = {
            id: 0,
            stockId: d.stockId,
            quantity: d.quantity,
            unitPrice: d.unitPrice,
            conversion: d.conversion,
            baseUnitId: d.baseUnitId,
            packageUnitId: d.packageUnitId,
            qtId: d.qtId,
            qtNo: d.qtNo,
            productName: d.productName,
            productCode: d.productCode,
            lotNumber: d.lotNumber,
          };
          return orderItem;
        });
        values.orderDate = dayjs(values.orderDate).toDate();
        values.docDate = dayjs(values.docDate).toDate();
        values.taxRate = vatRate;
        values.vatAmount = vatPrice;
        values.discountAmount = discount;
        values.creditDebitNoteItems = dataItem;
        values.reason = ECndnReason.CREDIT_PRICE;
        values.businessUnitName = userInfo?.buName;
        console.log('value', values);
        await mutation.mutateAsync(values);
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  useEffect(() => {
    if (orderData) {
      const initialCndnItems =
        orderData?.map(o => {
          const cndnItem: ICndnItem = {
            ...o,
            id: o.id,
            key: uuidv4(),
            orderQuantity: Number(o.quantity),
            orderUnitPrice: Number(o.unitPrice),
            orderItemId: Number(o.id),
            unitPrice: Number(o.unitPrice) ?? 0,
            quantity: Number(o.quantity),
          } as ICndnItem;
          return cndnItem;
        }) || [];

      setDataItem(initialCndnItems);
    }
  }, [orderData]);

  useEffect(() => {
    if (userInfo?.id && userInfo.role === EUserRole.Employee) {
      mainForm.setFieldsValue({
        salesId: userInfo.id,
      });
    }
  }, [userInfo, mainForm]);
  const onCustomerChange = (customerInfo: ICustomer) => {
    mainForm.setFieldsValue({
      customerId: customerInfo.id,
      customerName: customerInfo.name,
      shippingAddressId: undefined,
      creditTerm: customerInfo.creditTerm,
      logisticRemark: customerInfo.logisticRemark,
      whRemark: customerInfo.whRemark,
      billingRemark: customerInfo.billingRemark,
      salesId: customerInfo.salesId,
    });
  };
  const _customerId = Form.useWatch('customerId', mainForm);
  const _shippingAddressId = Form.useWatch('shippingAddressId', mainForm);
  const _shippingAddressText = Form.useWatch('shippingAddressText', mainForm);
  const _salesId = Form.useWatch('salesId', mainForm) || undefined;

  let totalPrice = 0;
  let summaryPrice = 0;
  dataItem?.forEach(({ orderQuantity, unitPrice }) => {
    totalPrice += Number(round(Number(orderQuantity) * Number(unitPrice), 2));
  });
  const actualPrice =
    _type == ECndnType.CREDIT || _type == ECndnType.CREDIT_RETURN
      ? round(Number(orderSummary) - totalPrice, 2)
      : round(Number(orderSummary) + totalPrice, 2);
  const vatPrice = round(
    ((Math.abs(totalPrice) - discount) * vatRate) / 100,
    2,
  );
  summaryPrice = Math.abs(totalPrice) - discount + vatPrice;

  console.log('dataItem => ', dataItem);

  return (
    <Card>
      <BreadcrumbComponent
        items={[
          {
            title: (
              <div
                className="cursor-pointer"
                onClick={() => navigate(`/cndn-free-form`)}
              >
                ใบเพิ่มหนี้/ลดหนี้
              </div>
            ),
          },
          {
            title: 'สร้างใหม่',
          },
        ]}
      />
      <div className="flex w-full mt-5">
        <Form
          form={mainForm}
          layout="vertical"
          className="w-full"
          autoComplete="off"
          initialValues={{
            type: ECndnType.CREDIT,
          }}
        >
          <Form.Item<ICndnFreeForm> name="customerCode" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item<ICndnFreeForm> name="salesName" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item<ICndnFreeForm> name="businessUnitId" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item<ICndnFreeForm> name="businessUnitName" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item<ICndnFreeForm> name="customerName" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item<ICndnFreeForm> name="whRemark" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item<ICndnFreeForm> name="logisticRemark" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item<ICndnFreeForm> name="shipToRemark" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item<ICndnFreeForm> name="billingRemark" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item<ICndnFreeForm> name="shippingAddressText" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={24} md={4}>
              <Form.Item<Partial<ICndnFreeForm>>
                label="วันที่"
                name="orderCreateDate"
                rules={[{ required: true, message: 'กรุณาเลือกวันที่' }]}
              >
                <DatePicker
                  allowClear={false}
                  size="large"
                  className="w-full"
                  locale={locale}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label="ลูกค้า"
                name="customerId"
                rules={[{ required: true, message: 'เลือกลูกค้า' }]}
              >
                <CustomerSelector handleSelectData={onCustomerChange} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item<ICndnFreeForm>
                label="ที่จัดส่ง"
                name="shippingAddressId"
                rules={[{ required: true, message: 'เลือกที่จัดส่ง' }]}
              >
                <ShippingAddressSelector
                  value={_shippingAddressId}
                  customerId={_customerId}
                  handleSelectData={(id, item) => {
                    mainForm.setFieldsValue({
                      shippingAddressId: id,
                      shipToRemark: item?.remark,
                      shippingAddressText: getShippingFullAddress(item),
                    });
                  }}
                />
                {_shippingAddressText && (
                  <div className="text-blue-700">{_shippingAddressText}</div>
                )}
              </Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item<ICndnFreeForm>
                label="ประเภท"
                name="type"
                rules={[{ required: true, message: 'เลือกประเภท' }]}
              >
                <Select
                  allowClear
                  className="md:min-w-[150px]"
                  size="large"
                  placeholder="เลือกประเภท"
                  options={getCndnTypeOptionsWithCreditReturn()}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item<ICndnFreeForm>
                label="เลขที่ใบขอเพิ่มหนี้/ลดหนี้"
                name="code"
                rules={[
                  { required: true, message: 'เลขที่ใบขอเพิ่มหนี้/ลดหนี้' },
                ]}
              >
                <Input size="large" placeholder="เลขที่ใบขอเพิ่มหนี้/ลดหนี้" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={6} xxl={4}>
              <Form.Item<ICndnFreeForm>
                label="เลขที่ใบสั่งสินค้า (CO)"
                name="soNo"
              >
                <Input size="large" placeholder="เลขที่ใบสั่งสินค้า" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={6} xxl={4}>
              <Form.Item<ICndnFreeForm>
                label="เลขที่ใบแจ้งหนี้ (IV)"
                name="ivNo"
              >
                <Input size="large" placeholder="เลขที่ใบแจ้งหนี้" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={6} xxl={4}>
              <Form.Item<ICndnFreeForm>
                label="เลขที่อ้างอิงใบสั่งซื้อ (PO)"
                name="poNo"
              >
                <Input size="large" placeholder="เลขที่ใบสั่งซื้อ" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={6} xxl={4}>
              <Form.Item<Partial<ICndnFreeForm>>
                label="พนักงานขาย"
                name="salesId"
                rules={[{ required: true, message: 'เลือกพนักงานขาย' }]}
              >
                <UserSelector
                  value={_salesId}
                  handleSelectData={(id, user) => {
                    console.log(user);
                    mainForm.setFieldsValue({
                      salesId: id,
                      salesName: `${user?.firstName}`,
                      businessUnitId: user?.businessUnitId,
                      businessUnitName: user?.bu,
                    });
                  }}
                  businessUnitId={userInfo?.bu || undefined}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
      <Title
        customClassName="text-lg text-blue-700 mt-4 mb-1"
        customContainerClassName="py-0"
        title={`รายการตามใบแจ้งหนี้`}
      />
      <OrderCndnFreeFomTable
        form={form}
        _customerId={_customerId}
        handleOrderItem={(
          orderItem,
          orderSummary,
          orderVatRate,
          orderDiscount,
        ) => {
          setOrderData(orderItem);
          setOrderSummary(orderSummary);
          setVatRate(orderVatRate);
          setDiscount(Number(orderDiscount || 0));
        }}
      />
      {orderData.length > 0 && _type && (
        <>
          <Title
            customClassName="text-lg text-blue-700 mt-4 mb-1"
            customContainerClassName="py-0"
            title={`รายการเพิ่มหนี้/ลดหนี้`}
          />
          <div className="flex w-full mt-2">
            <Form form={form} component={false}>
              <Table
                components={{
                  body: {
                    cell: EditableCell,
                  },
                }}
                locale={{
                  emptyText: 'เพิ่มรายการใหม่',
                }}
                size="small"
                bordered
                dataSource={dataItem}
                columns={mergedColumns}
                className="w-full"
                rowClassName="editable-row"
                pagination={false}
                scroll={{ x: 1200 }}
                summary={() => {
                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row>
                        <Table.Summary.Cell
                          index={0}
                          colSpan={9}
                          className="font-bold items-end"
                          align="right"
                        >
                          มูลค่าตามใบแจ้งหนี้เดิม
                        </Table.Summary.Cell>

                        <Table.Summary.Cell
                          index={7}
                          colSpan={1}
                          className="font-bold items-end"
                          align="right"
                        >
                          {formatNumber(Number(orderSummary), 2)}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={0} />
                      </Table.Summary.Row>
                      <Table.Summary.Row>
                        <Table.Summary.Cell
                          index={0}
                          colSpan={9}
                          className="font-bold items-end"
                          align="right"
                        >
                          มูลค่าที่ถูกต้อง
                        </Table.Summary.Cell>

                        <Table.Summary.Cell
                          index={7}
                          colSpan={1}
                          className="font-bold items-end"
                          align="right"
                        >
                          {formatNumber(Number(actualPrice), 2)}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={0} />
                      </Table.Summary.Row>
                      <Table.Summary.Row>
                        <Table.Summary.Cell
                          index={0}
                          colSpan={9}
                          className="font-bold items-end"
                          align="right"
                        >
                          ผลต่าง
                        </Table.Summary.Cell>

                        <Table.Summary.Cell
                          index={7}
                          colSpan={1}
                          className="font-bold items-end"
                          align="right"
                        >
                          {formatNumber(totalPrice, 2)}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={0} />
                      </Table.Summary.Row>
                      <Table.Summary.Row>
                        <Table.Summary.Cell
                          index={0}
                          colSpan={9}
                          className="font-bold items-end"
                          align="right"
                        >
                          ส่วนลด (บาท)
                        </Table.Summary.Cell>
                        <Table.Summary.Cell
                          index={5}
                          colSpan={1}
                          className="font-bold items-end"
                          align="right"
                        >
                          <InputNumber
                            className="w-full"
                            value={discount}
                            style={{ textAlignLast: 'right' }}
                            controls={false}
                            decimalSeparator="."
                            formatter={value =>
                              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                            }
                            parser={value =>
                              value?.replace(
                                /\$\s?|(,*)/g,
                                '',
                              ) as unknown as number
                            }
                            precision={2}
                            onChange={value => {
                              setDiscount(value || 0);
                            }}
                            // max={totalPrice}
                            min={0}
                          />
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={0} />
                      </Table.Summary.Row>
                      <Table.Summary.Row>
                        <Table.Summary.Cell
                          index={0}
                          colSpan={9}
                          className="font-bold items-end"
                          align="right"
                        >
                          ยอดรวมหลังหักส่วนลด
                        </Table.Summary.Cell>
                        <Table.Summary.Cell
                          index={7}
                          colSpan={1}
                          className="font-bold items-end"
                          align="right"
                        >
                          {formatNumber(Math.abs(totalPrice) - discount, 2)}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={0} />
                      </Table.Summary.Row>
                      <Table.Summary.Row>
                        <Table.Summary.Cell
                          index={0}
                          colSpan={9}
                          className="font-bold items-end"
                          align="right"
                        >
                          ภาษีมูลค่าเพิ่ม{' '}
                          <InputNumber
                            value={vatRate}
                            onChange={value => {
                              setVatRate(value || 0);
                            }}
                            disabled
                            min={0}
                            suffix="%"
                          />
                        </Table.Summary.Cell>
                        <Table.Summary.Cell
                          index={7}
                          colSpan={1}
                          className="font-bold items-end"
                          align="right"
                        >
                          {formatNumber(vatPrice, 2)}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={0} />
                      </Table.Summary.Row>
                      <Table.Summary.Row>
                        <Table.Summary.Cell
                          index={0}
                          colSpan={9}
                          className="font-bold items-end"
                          align="right"
                        >
                          ยอดสุทธิ
                        </Table.Summary.Cell>
                        <Table.Summary.Cell
                          index={7}
                          colSpan={1}
                          className="font-bold items-end"
                          align="right"
                        >
                          {formatNumber(summaryPrice, 2)}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={0} />
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
            </Form>
          </div>
          <div className="mt-2">
            <Form
              form={mainForm}
              layout="vertical"
              className="w-full"
              autoComplete="off"
            >
              <Form.Item<ICndnFreeForm>
                name="remark"
                label="สาเหตุ"
                rules={[{ required: true }]}
              >
                <Input.TextArea rows={2} />
              </Form.Item>
            </Form>
          </div>
          <div className="flex justify-end mt-5 gap-2">
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              className=" font-bold min-w-[150px]"
              onClick={() => handleUpdate()}
              loading={mutation.isPending}
            >
              บันทึก
            </Button>
          </div>
        </>
      )}
    </Card>
  );
};

export default CreateCndnFreeForm;
